/**
 * Anordnung der Guide-Spalte je Person — Zwischenspeicher im Browser, letztes Wort am Server.
 *
 * Ablauf: beim Start steht sofort, was dieser Browser zuletzt kannte (sonst die alten
 * Griff-Merker `guide-breite`/`guide-hoehe`, sonst die Vorgabe „uebereinander"). Dann
 * kommt der Serverstand und gewinnt — ausser die Person hat inzwischen selbst etwas
 * verstellt; dann gilt ihre Hand, und die geht ohnehin gleich an den Server.
 * Jede Aenderung wird sofort lokal gemerkt und gebuendelt (600 ms) an den Server
 * geschickt. Scheitert das, steht es sichtbar an der Spalte (`fehler`).
 */
import { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import {
  GUIDE_ANORDNUNG_VORGABE, GUIDE_SPALTE_MINDEST_AB_PX, guideAnordnungReduzieren, leseGuideAnordnung, begrenzeGroesse,
  type GuideAnordnung, type GuideAnordnungAktion,
} from '../../shared/guide-anordnung';

const API = '/api/preferences/guide-anordnung';
const merkerFuer = (person: string) => `guide-anordnung-v1:${person}`;

function ausZwischenspeicher(person: string): GuideAnordnung {
  try {
    const roh = localStorage.getItem(merkerFuer(person));
    const s = roh ? leseGuideAnordnung(JSON.parse(roh)) : null;
    if (s) return s;
    // Uebergang: die bisherigen Griff-Merker dieses Browsers nicht verlieren.
    const b = Number(localStorage.getItem('guide-breite'));
    const h = Number(localStorage.getItem('guide-hoehe'));
    return {
      ...GUIDE_ANORDNUNG_VORGABE,
      ...(Number.isFinite(b) && b > 5 && b < 95 ? { guideBreite: begrenzeGroesse('guideBreite', b) } : {}),
      ...(Number.isFinite(h) && h > 5 && h < 95 ? { guideHoehe: begrenzeGroesse('guideHoehe', h) } : {}),
    };
  } catch (err) {
    console.warn('[guide-anordnung] Zwischenspeicher nicht lesbar — Vorgabe:', err);
    return { ...GUIDE_ANORDNUNG_VORGABE };
  }
}

/** `person`: die angemeldete Kennung, ohne Anmeldung ein fester Name. Der Server nimmt ohnehin nur seine eigene. */
export function useGuideAnordnung(person: string) {
  const schluessel = person;
  const [stand, dispatch] = useReducer(guideAnordnungReduzieren, schluessel, ausZwischenspeicher);
  const [fehler, setFehler] = useState<string | null>(null);
  const eigeneHand = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  // Serverstand holen — er gewinnt beim Start.
  useEffect(() => {
    let ab = false;
    fetch(API, { credentials: 'same-origin', signal: AbortSignal.timeout(10_000) })
      .then(async r => {
        const d = await r.json().catch(() => null) as { gespeichert?: boolean; anordnung?: unknown; error?: string } | null;
        if (!r.ok) throw new Error(d?.error || `HTTP ${r.status}`);
        if (ab) return;
        if (!d?.gespeichert) return;                     // nichts gespeichert: Zwischenspeicher/Vorgabe bleibt
        const s = leseGuideAnordnung(d.anordnung);
        if (!s) throw new Error('Server lieferte eine unlesbare Anordnung');
        if (eigeneHand.current) return;                  // die Person hat schon verstellt — ihre Hand gilt
        dispatch({ typ: 'vomServer', stand: s });
        setFehler(null);
      })
      .catch(e => { if (!ab) setFehler(`Anordnung vom Server nicht geladen: ${e instanceof Error ? e.message : String(e)}`); });
    return () => { ab = true; };
  }, [schluessel]);

  // Zwischenspeicher sofort, Server gebuendelt — aber erst, wenn die Person selbst etwas verstellt hat.
  const letzter = useRef(stand);
  useEffect(() => {
    if (letzter.current === stand) return;
    letzter.current = stand;
    try { localStorage.setItem(merkerFuer(schluessel), JSON.stringify(stand)); } catch { /* Server bleibt massgeblich */ }
    if (!eigeneHand.current) return;
    window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => {
      fetch(API, {
        method: 'PUT', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(stand), signal: AbortSignal.timeout(10_000),
      })
        .then(async r => {
          if (r.ok) { setFehler(null); return; }
          const d = await r.json().catch(() => null) as { error?: string } | null;
          throw new Error(d?.error || `HTTP ${r.status}`);
        })
        .catch(e => setFehler(`Anordnung nicht am Server gespeichert (gilt nur in diesem Browser): ${e instanceof Error ? e.message : String(e)}`));
    }, 600);
  }, [stand, schluessel]);
  useEffect(() => () => window.clearTimeout(timer.current), []);

  const aendern = useCallback((a: GuideAnordnungAktion) => {
    eigeneHand.current = true;
    dispatch(a);
  }, []);

  return { stand, aendern, fehler };
}

/**
 * Ist das Fenster breit genug fuer die 420-px-Untergrenze der Guide-Spalte?
 * Dieselbe Grenze wie die Media-Query in `responsive.css` — dort haelt das CSS die Breite,
 * hier erfaehrt der Griff davon, damit er an derselben Stelle stoppt.
 */
export function useBreitesFenster(): boolean {
  const abfrage = `(min-width: ${GUIDE_SPALTE_MINDEST_AB_PX}px)`;
  const [breit, setBreit] = useState(() => typeof window !== 'undefined' && !!window.matchMedia?.(abfrage).matches);
  useEffect(() => {
    const mq = window.matchMedia?.(abfrage);
    if (!mq) return;
    const neu = () => setBreit(mq.matches);
    neu();
    mq.addEventListener('change', neu);
    return () => mq.removeEventListener('change', neu);
  }, [abfrage]);
  return breit;
}
